"use client";
import React, { useState } from "react";
import { Database, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "react-toastify";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import styles from "./Styles/NewConnection.module.css";
import { useDispatch, useSelector } from "react-redux";
import { saveConnection } from "../../actions/saveConnection";
import { AppSidebar } from "./ui/sidebar";
import Header from "../header";
import { decryptData } from "../utils/EncriptionStore";

const formSchema = z.object({
  db_type: z.enum(["postgres", "mysql", "mongodb", "sqlite"], {
    required_error: "Database type is required.",
  }),
  host: z.string().min(1, { message: "Host is required." }),
  port: z
    .string()
    .min(1, { message: "Port is required." })
    .refine((val) => !isNaN(Number(val)), {
      message: "Port must be a number.",
    }),
  schema: z.string().min(1, { message: "Schema name is required." }),
  username: z.string().min(1, { message: "Username is required." }),
  password: z.string().min(1, { message: "Password is required." }),
});

export default function NewConnectionForm() {
  const user = (() => {
    const encryptedData = localStorage.getItem("profile");
    return encryptedData ? decryptData(encryptedData) : null;
  })();
  const router = useNavigate();
  const dispatch = useDispatch();
  const { loading, error, success, message } = useSelector(
    (state) => state.saveConnection
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      db_type: "mysql",
      host: "",
      port: "",
      schema: "",
      username: "",
      password: "",
    },
  });

  // const onSubmit = async (values) => {
  //   try {
  //     await dispatch(
  //       saveConnection(values, {
  //         customer_id: user?.customer_id,
  //         group_id: user?.group_id,
  //       })
  //     );
  //     toast.success("Connection saved successfully!", {
  //       position: "top-right",
  //       autoClose: 3000,
  //     });
  //     router("/schema_explorer");
  //   } catch (err) {
  //     toast.error(err.message || "Failed to save connection.");
  //   }
  // };

  const onSubmit = async (values) => {
  try {
    // merge customer_id and group_id into the payload
    const payload = {
      ...values,
      customer_id: user?.customer_id,
      group_id: user?.group_id,
      customer_name: user?.customer_name,
      group_name: user?.groupname,
    };

    await dispatch(saveConnection(payload));

    toast.success("Connection saved successfully!", {
      position: "top-right",
      autoClose: 3000,
    });

    router("/schema_explorer");
  } catch (err) {
    toast.error(err.message || "Failed to save connection.");
  }
};


  const [isOpen, setIsOpen] = useState(true);
  const toggleSidebar = () => setIsOpen((prev) => !prev);

  return (
    <div className={styles.NewConnectionWrapper}>
      <div className={styles.twoSidebars}>
        <Header />
        <div
          className={` ${isOpen ? styles.sidebarOpen : styles.sidebarClosed}`}
        >
          <AppSidebar isOpen={isOpen} toggleSidebar={toggleSidebar} />
        </div>
      </div>

      <Card className={`${styles.dashboardLayout} ${styles.Card}`}>
        <CardHeader>
          <CardTitle className={styles.CardTitle}>
            <Database className="h-5 w-5" />
            <span>Create new Connection</span>
          </CardTitle>
          <CardDescription>
            Enter your database connection details. All information is stored
            securely.
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className={styles.Form}
            >
              <FormField
                control={form.control}
                name="db_type"
                render={({ field }) => (
                  <div className={styles.formItem}>
                    <FormLabel>Database Type</FormLabel>
                    <div className={styles.selectControl}>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className={styles.selectTrigger}>
                            <SelectValue placeholder="Select database type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="postgres">PostgreSQL</SelectItem>
                          <SelectItem value="mysql">MySQL</SelectItem>
                          <SelectItem value="mongodb">MongoDB</SelectItem>
                          <SelectItem value="sqlite">SQLite</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <FormMessage />
                  </div>
                )}
              />

              {["host", "port", "schema", "username", "password"].map(
                (fieldName) => (
                  <FormField
                    key={fieldName}
                    control={form.control}
                    name={fieldName}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {fieldName.charAt(0).toUpperCase() +
                            fieldName.slice(1)}
                        </FormLabel>
                        <FormControl>
                          <Input
                            type={
                              fieldName === "password" ? "password" : "text"
                            }
                            placeholder={
                              fieldName === "host"
                                ? "localhost or db.example.com"
                                : fieldName === "port"
                                ? "5432"
                                : fieldName === "schema"
                                ? "my_database"
                                : fieldName === "username"
                                ? "db_user"
                                : "••••••••"
                            }
                            {...field}
                            className={`${styles.formTextarea} form-control`}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )
              )}

              {error && <p className="text-sm text-red-500 mt-2">{error}</p>}

              <Button type="submit" className="gap-2" disabled={loading}>
                {loading ? (
                  "Saving connection..."
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span> Save Connection</span>
                  </>
                )}
              </Button>
            </form>
          </Form>
        </CardContent>

        <CardFooter className="border-t bg-muted/50 px-6 py-3">
          <p className="text-xs text-muted-foreground">
            Your connection details are encrypted and stored securely. We never
            share your database credentials.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
